import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Separator, SidebarTrigger } from '@erp/ui';
import { Receipt } from '@/components/icons';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useCompanySettings } from '@/hooks/use-settings';
import { useEnabledModules } from '@/hooks/use-modules';
import { useBrandTheme } from '@/lib/brand-theme-provider';
import { NotificationBell, OrderNotificationsBell } from './NotificationBell';
import { SyncStatusBadge } from './SyncStatusBadge';
import { ConflictBadge } from './ConflictBadge';
import { QuickLinksBar } from './QuickLinksBar';
import { DefaultBrandMark } from '@/components/DefaultBrandMark';
import { getBrandInitial } from '@/lib/default-brand';

export function Header() {
  const { t } = useTranslation();
  const settingsQuery = useCompanySettings();
  const { data: settings } = settingsQuery;
  const { enabled: enabledModules } = useEnabledModules();
  const { resolvedMode } = useBrandTheme();
  const brandingResolved = settingsQuery.isSuccess || settingsQuery.isError;
  const brandName = brandingResolved ? settings?.softwareName || t('app.name') : '';
  const brandInitial = getBrandInitial(brandName);
  const posEnabled = enabledModules.has('caisse');
  const headerLogoUrl =
    brandingResolved && resolvedMode === 'dark'
      ? settings?.logoDarkUrl ?? settings?.logoUrl ?? null
      : brandingResolved
        ? settings?.logoUrl ?? settings?.logoDarkUrl ?? null
        : null;

  return (
    <header className="flex h-15 shrink-0 items-center gap-2 border-b border-border/60 bg-background/80 px-4 backdrop-blur-xl supports-[backdrop-filter]:bg-background/70">
      <SidebarTrigger className="-ms-1" />
      <Separator orientation="vertical" className="me-2 data-[orientation=vertical]:h-4" />
      <QuickLinksBar />
      <div className="flex items-center gap-2 min-w-0 flex-1 md:hidden">
        {!brandingResolved ? (
          <div className="h-6 w-28 animate-pulse rounded-md bg-muted" aria-hidden="true" />
        ) : headerLogoUrl ? (
          <img src={headerLogoUrl} alt={brandName} className="h-6 object-contain" />
        ) : (
          <DefaultBrandMark
            name={brandName}
            initial={brandInitial}
            iconClassName="size-6 rounded-[8px] text-[11px]"
            textClassName="text-sm font-semibold"
          />
        )}
      </div>
      <div className="ms-auto flex items-center gap-1">
        {posEnabled && (
          <>
            <Button
              variant="ghost"
              size="sm"
              asChild
              aria-label={t('nav.posScreen')}
              title={t('nav.posScreen')}
              className="h-8 gap-1.5 text-primary hover:bg-primary/10 hover:text-primary"
            >
              <Link to="/caisse/pos">
                <Receipt size={16} />
                <span className="hidden sm:inline">{t('header.openPos')}</span>
              </Link>
            </Button>
            <Separator orientation="vertical" className="mx-1 data-[orientation=vertical]:h-4" />
          </>
        )}
        <ConflictBadge />
        <SyncStatusBadge />
        <OrderNotificationsBell />
        <NotificationBell />
        <ThemeToggle />
      </div>
    </header>
  );
}
